import { useEffect, useMemo, useRef } from 'react';

import { ToolIdType, ProductIdType } from '@adsk/alloy-provider';

import { useTranslation } from '../i18n';
import { getTabTitle } from './helpers/getTabTitle';
import { toolIdToTranslatedKey } from './helpers/toolIdToTranslatedKey';

export type TabTitleProps = {
  productId?: ProductIdType | null;
  toolId?: ToolIdType | null;
  value?: string;
  restoreOriginalTitle?: boolean;
};

export const HeadlessTabTitle = ({
  productId,
  toolId,
  value,
  restoreOriginalTitle = true,
}: TabTitleProps) => {
  const { current: oldTitle } = useRef(document.title);
  const { t } = useTranslation();
  const translatedToolId = useMemo(() => {
    if (!toolId) {
      return undefined;
    }

    const translationKey = toolIdToTranslatedKey(toolId);
    const translatedToolTitle = t(translationKey)?.toString();

    if (translatedToolTitle === translationKey) {
      console.warn(`Translation for ${toolId} is missing`);
      return undefined;
    }

    return translatedToolTitle;
  }, [t, toolId]);

  const title = useMemo(
    () =>
      getTabTitle({
        productId,
        toolTitle: translatedToolId,
        additionalInformation: value,
      }),
    [translatedToolId, productId, value],
  );

  useEffect(() => {
    document.title = title;
  }, [title]);

  useEffect(() => {
    return () => {
      if (restoreOriginalTitle) {
        document.title = oldTitle;
      }
    };
  }, [oldTitle, restoreOriginalTitle]);

  return null;
};
